import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import type { Agency } from "@shared/schema";
import { useAgencies } from "./api";

interface AgencyContextType {
  agencies: Agency[];
  selectedAgency: Agency | null;
  selectAgency: (agency: Agency | null) => void;
  isLoading: boolean;
  isAllAgencies: boolean;
  setAllAgencies: (all: boolean) => void;
}

const AgencyContext = createContext<AgencyContextType | undefined>(undefined);

export function AgencyProvider({ children }: { children: ReactNode }) {
  const { data: agencies, isLoading } = useAgencies();
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [isAllAgencies, setIsAllAgencies] = useState(true);

  useEffect(() => {
    // Set the first agency as default when agencies load
    if (agencies && agencies.length > 0 && !selectedAgency && !isAllAgencies) {
      setSelectedAgency(agencies[0]);
    }
  }, [agencies, selectedAgency, isAllAgencies]);

  const selectAgency = (agency: Agency | null) => {
    setSelectedAgency(agency);
    setIsAllAgencies(agency === null);
  };

  const setAllAgencies = (all: boolean) => {
    setIsAllAgencies(all);
    if (all) {
      setSelectedAgency(null);
    } else if (agencies && agencies.length > 0) {
      setSelectedAgency(agencies[0]);
    }
  };

  return (
    <AgencyContext.Provider
      value={{
        agencies: agencies || [],
        selectedAgency,
        selectAgency,
        isLoading,
        isAllAgencies,
        setAllAgencies,
      }}
    >
      {children}
    </AgencyContext.Provider>
  );
}

export function useAgencyContext() {
  const context = useContext(AgencyContext);
  if (context === undefined) {
    throw new Error("useAgencyContext must be used within an AgencyProvider");
  }
  return context;
}
